/**
 * 개인 휴가 페이지
 *
 * 내 휴가 잔액, 사용 내역, 신청 내역을 확인하고 새로운 휴가를 신청할 수 있는 페이지
 */

import React, { useState, useEffect } from 'react';
import { ModalOverlay } from '../../shared/components/ModalOverlay';
import { Calendar, Clock, TrendingDown, Gift, History, Bell, Flower2, Cake, Stethoscope } from 'lucide-react';
import { Timestamp, doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getDb, getFunctions } from '../../firebase/utils';
import { useMyLeaveBalance } from '../../shared/hooks/useLeaveBalances';
import { useMyLeaveRequests } from '../../shared/hooks/useLeaveRequests';
import { useLeaveMutations } from '../../shared/hooks/useLeaveMutations';
import { useLeaveTypes } from '../../shared/hooks/useLeaveTypes';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { DEFAULT_ALLOWED_UNITS } from '../../shared/constants/leaveUnits';
import { LeaveRequestInput } from '../../shared/types/leaveRequest';
import { LeaveTypeCode, LeaveUnit, LeaveType } from '../../shared/types/leaveType';
import { LeaveHistory } from '../../shared/types/leaveHistory';
import { QUARTER_LABELS } from '../../shared/types/personalHealingDay';
// V44: 내 휴가 현황 테이블 컴포넌트
import { MyLeaveStatusTable } from './components/MyLeaveStatusTable';
import { filterVisibleLeaveTypes } from '../../shared/utils/leaveTypeEligibility';
import { getLeaveUnitLabel } from '../../shared/utils/leaveUnitLabel';  // V69
import { useLeaveUpload, LeaveAttachment } from '../../shared/hooks/useLeaveUpload';  // V70
import { useApprovedWorkSchedule } from '../../shared/hooks/useApprovedWorkSchedule';  // V60.6.2
import { WorkSchedule } from '../../shared/types/workSchedule';  // V60.6.2
import { formatCompOffDaysHours } from '../../shared/utils/formatCompOff';  // V78.1
import { useCalendarHolidays } from '../../shared/hooks/useHolidays';  // V66 (boundary)

/**
 * V33.4: 사용 단위 라벨 맵
 */
const UNIT_LABELS: Record<LeaveUnit, string> = {
  fullDay: '전일 (1일)',
  halfDay: '반차 (0.5일)',
  morningHalf: '오전반차 (0.5일)',
  afternoonHalf: '오후반차 (0.5일)',
  hourly: '시간단위',
};

/**
 * V62: 점심시간을 건너뛰어 종료시간 계산
 * workHours = 실제 사용(근무)시간, 점심 구간은 자동 스킵
 *
 * 예) lunchStart=12:00, lunchEnd=13:00
 *   start=11:00, work=2h → 11:00~12:00(1h) + skip lunch + 13:00~14:00(1h) → end=14:00
 *   start=11:30, work=1h → 11:30~12:00(30m) + skip lunch + 13:00~13:30(30m) → end=13:30
 *   start=12:00, work=1h → effectiveStart=13:00 + 1h → end=14:00
 *   start=14:00, work=1h → 14:00+1h → end=15:00
 */
function calcEndTimeSkippingBreaks(
  startTime: string,
  workHours: number,
  lunchStart?: string,
  lunchEnd?: string
): string {
  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const fromMin = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

  const startMin = toMin(startTime);
  const requiredMin = workHours * 60;

  if (!lunchStart || !lunchEnd) {
    return fromMin(startMin + requiredMin);
  }

  const lStartMin = toMin(lunchStart);
  const lEndMin = toMin(lunchEnd);

  // start >= lunchEnd → after lunch, simple add
  if (startMin >= lEndMin) {
    return fromMin(startMin + requiredMin);
  }

  // start >= lunchStart → during lunch, push to lunchEnd
  if (startMin >= lStartMin) {
    return fromMin(lEndMin + requiredMin);
  }

  // start < lunchStart → before lunch
  const beforeLunch = lStartMin - startMin;
  if (beforeLunch >= requiredMin) {
    return fromMin(startMin + requiredMin);
  }
  const remaining = requiredMin - beforeLunch;
  return fromMin(lEndMin + remaining);
}

/**
 * V60.6.2: workSchedule에서 특정 날짜의 근무시간 조회
 * dailyOverrides 우선, 없으면 base 사용
 */
function getScheduleTimesForDate(
  ws: WorkSchedule | null,
  dateStr: string
): { startTime: string; endTime: string; lunchStart: string; lunchEnd: string } | null {
  if (!ws) return null;
  const override = ws.dailyOverrides?.[dateStr];
  return {
    startTime: override?.startTime ?? ws.base.startTime,
    endTime: override?.endTime ?? ws.base.endTime,
    lunchStart: override?.lunchStart ?? ws.base.lunchStart,
    lunchEnd: override?.lunchEnd ?? ws.base.lunchEnd,
  };
}

/**
 * V62: 근무시간 범위 내에서 시작시간 옵션 생성 (30분 단위)
 * 점심시간 스킵 기반 종료시간이 scheduleEnd 이내인 시작시간만 포함
 */
function generateStartTimeOptions(
  scheduleStart: string,
  scheduleEnd: string,
  hours: number,
  lunchStart?: string,
  lunchEnd?: string
): string[] {
  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
  const startMin = toMin(scheduleStart);
  const endMin = toMin(scheduleEnd);
  const options: string[] = [];
  for (let m = startMin; m < endMin; m += 30) {
    const fromMin = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
    const startStr = fromMin(m);
    const calcEnd = calcEndTimeSkippingBreaks(startStr, hours, lunchStart, lunchEnd);
    if (toMin(calcEnd) <= endMin) {
      options.push(startStr);
    }
  }
  return options;
}

// V282-1: DEFAULT_ALLOWED_UNITS 상수 사용 (shared/constants/leaveUnits.ts)

/**
 * V33.5: UI용 사용 단위 변환
 *
 * allowedUnits에 'halfDay'가 있으면 UI에서는 노출하지 않고
 * 'morningHalf'와 'afternoonHalf'로 분해하여 표시합니다.
 *
 * @param allowedUnits 원본 allowedUnits 배열
 * @returns UI에 표시할 단위 배열 (순서: fullDay → morningHalf → afternoonHalf → hourly)
 */
function expandAllowedUnitsForUI(allowedUnits: LeaveUnit[]): LeaveUnit[] {
  const result = new Set<LeaveUnit>();

  for (const unit of allowedUnits) {
    if (unit === 'halfDay') {
      // halfDay는 morningHalf + afternoonHalf로 분해
      result.add('morningHalf');
      result.add('afternoonHalf');
    } else {
      result.add(unit);
    }
  }

  // 정렬: fullDay → morningHalf → afternoonHalf → hourly
  const sortOrder: LeaveUnit[] = ['fullDay', 'morningHalf', 'afternoonHalf', 'hourly'];
  return sortOrder.filter((unit) => result.has(unit));
}

export function MyLeavePage() {
  const { user } = useCurrentUser();
  const { balance, isLoading: balanceLoading } = useMyLeaveBalance();
  const { requests, isLoading: requestsLoading } = useMyLeaveRequests();
  // V68: 회사별 노출 필터 — KR 직원 → KR+COMMON, CN 직원 → CN+COMMON
  const { leaveTypes } = useLeaveTypes(user?.profile?.companyCode === 'CN' ? 'CN' : 'KR');
  // V69: 자격 필터 — shared helper SSoT (WorkMyApprovalsPage 와 동일 사용)
  const _cc = user?.profile?.companyCode;
  const _etc = (user?.profile as any)?.currentEmploymentTypeCode;
  const _gender = (user?.profile as any)?.gender;
  const isCnNonRegularForPH = _cc === 'CN' && _etc !== 'REGULAR';

  const visibleLeaveTypes = filterVisibleLeaveTypes(leaveTypes, {
    companyCode: _cc,
    currentEmploymentTypeCode: _etc,
    gender: _gender,
  });
  // V55: 내 휴가관리 페이지에서 취소 기능 제거 - 승인요청 페이지에서만 취소 가능
  const { createLeaveRequest, isSubmitting } = useLeaveMutations();
  // V55: refreshMyBalance 제거 - 취소 기능 제거됨
  const { uploadFile, isUploading } = useLeaveUpload();  // V70

  const [showRequestForm, setShowRequestForm] = useState(false);
  const [formData, setFormData] = useState<Partial<LeaveRequestInput>>({
    leaveTypeCode: 'annual',
    unit: 'fullDay',
    metadata: {},
  });
  const [uploadedAttachments, setUploadedAttachments] = useState<LeaveAttachment[]>([]);  // V70

  // V60.6: 대체휴무 시간단위 상태
  const [compOffHours, setCompOffHours] = useState(1);
  const [compOffStartTime, setCompOffStartTime] = useState('09:00');

  // V60.6.2: 대체휴무 시간단위 — 근무시간 SSoT 조회
  const leaveMonth = formData.startDate?.substring(0, 7) || '';
  const { workSchedule: leaveWorkSchedule } = useApprovedWorkSchedule(leaveMonth);

  // V66 (boundary): 본인 회사 공휴일 fetch — 시작/종료 휴일 차단용
  const myCompanyCode = (user?.profile as any)?.companyCode as ('KR' | 'CN' | null) ?? null;
  const startYear = formData.startDate ? parseInt(formData.startDate.slice(0, 4), 10) : new Date().getFullYear();
  const { holidays: companyHolidays } = useCalendarHolidays(myCompanyCode, startYear);
  const holidayDateSet = React.useMemo(
    () => new Set((companyHolidays || []).map((h: any) => h.date)),
    [companyHolidays],
  );

  // V66 (boundary): 단일 일자가 비근무일(주말/공휴일) 인지 검사 — 클라이언트 사이드 사전 차단
  const checkBoundaryReasons = (dateStr: string | undefined): string[] => {
    if (!dateStr || dateStr.length !== 10) return [];
    const reasons: string[] = [];
    const dow = new Date(dateStr).getDay();
    if (dow === 0 || dow === 6) reasons.push('주말');
    if (holidayDateSet.has(dateStr)) reasons.push('공휴일');
    return reasons;
  };
  const startBoundaryReasons = checkBoundaryReasons(formData.startDate);
  const endBoundaryReasons = checkBoundaryReasons(formData.endDate);
  const hasBoundaryError = startBoundaryReasons.length > 0 || endBoundaryReasons.length > 0;
  const leaveScheduleTimes = formData.startDate
    ? getScheduleTimesForDate(leaveWorkSchedule, formData.startDate)
    : null;
  const compOffStartOptions = leaveScheduleTimes
    ? generateStartTimeOptions(leaveScheduleTimes.startTime, leaveScheduleTimes.endTime, compOffHours, leaveScheduleTimes.lunchStart, leaveScheduleTimes.lunchEnd)
    : [];

  // Phase 6-2: 대체휴무 이력 조회
  const [compOffHistories, setCompOffHistories] = useState<LeaveHistory[]>([]);
  const [historiesLoading, setHistoriesLoading] = useState(false);

  // V75.2: 휴가알림 관리 모달
  const [showNotifModal, setShowNotifModal] = useState(false);
  const [extraEmails, setExtraEmails] = useState<string[]>([]);
  const [notifSaving, setNotifSaving] = useState(false);

  // V75.2: 휴가알림 설정 로드
  useEffect(() => {
    if (!user?.profile?.id) return;
    const loadExtraEmails = async () => {
      const userDoc = await getDoc(doc(getDb(), 'users', user.profile.id));
      if (userDoc.exists()) {
        setExtraEmails(userDoc.data()?.leaveNotificationExtraEmails || []);
      }
    };
    loadExtraEmails();
  }, [user?.profile?.id]);

  // V75.4: 휴가알림 설정 저장 (CF 경유 — users write rules가 false이므로)
  const handleSaveNotifEmails = async () => {
    if (!user?.profile?.id) return;
    setNotifSaving(true);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../../firebase/config');
      if (!functions) throw new Error('Firebase Functions not initialized');

      const fn = httpsCallable<{ emails: string[] }, { success: boolean }>(
        functions,
        'saveLeaveNotificationEmails'
      );
      const cleaned = Array.from(new Set(extraEmails.map(e => e.trim().toLowerCase()).filter(Boolean)));
      // V75.5: @ngle.co.kr 도메인 검증
      const invalid = cleaned.find(e => !e.endsWith('@ngle.co.kr'));
      if (invalid) {
        alert(`@ngle.co.kr 이메일만 입력할 수 있습니다.\n잘못된 주소: ${invalid}`);
        setNotifSaving(false);
        return;
      }
      await fn({ emails: cleaned });
      setExtraEmails(cleaned);
      alert('휴가 알림 수신자가 저장되었습니다.');
      setShowNotifModal(false);
    } catch (err) {
      console.error('[V75.4] 휴가알림 저장 실패:', err);
      alert('저장 중 오류가 발생했습니다.');
    } finally {
      setNotifSaving(false);
    }
  };

  // V33.4: 선택된 휴가유형 객체 조회
  const selectedLeaveType: LeaveType | undefined = leaveTypes.find(
    (lt) => lt.code === formData.leaveTypeCode
  );

  // V33.4/V282-1: 선택된 휴가유형의 allowedUnits (빈 배열/undefined 모두 방어)
  const allowedUnits: LeaveUnit[] =
    selectedLeaveType?.allowedUnits?.length ? selectedLeaveType.allowedUnits : DEFAULT_ALLOWED_UNITS;

  // V33.5: UI용 사용 단위 (halfDay → morningHalf + afternoonHalf 분해)
  const uiUnits: LeaveUnit[] = expandAllowedUnitsForUI(allowedUnits);

  // V60.6: 대체휴무 시간단위 표시 조건
  const isCompOffHourly = formData.leaveTypeCode === 'compensatory' && formData.unit === 'hourly';

  // V33.4/V33.5: leaveType 변경 시 unit 자동 교정 (uiUnits 기준)
  useEffect(() => {
    if (!formData.unit) return;

    // 현재 unit이 uiUnits에 없으면 첫 값으로 교정
    // (halfDay가 선택값으로 들어있는 레거시 케이스도 처리)
    if (!uiUnits.includes(formData.unit as LeaveUnit)) {
      setFormData((prev) => ({
        ...prev,
        unit: uiUnits[0],
      }));
    }
    // V60.6: 시간단위 선택 해제 시 시간 상태 초기화
    if (formData.unit !== 'hourly') {
      setCompOffHours(1);
      setCompOffStartTime('09:00');
    }
  }, [formData.leaveTypeCode, uiUnits, formData.unit]);

  // V60.6.2: 시간수/근무시간 변경 시 시작시간이 허용 범위를 벗어나면 자동 교정
  useEffect(() => {
    if (compOffStartOptions.length > 0 && !compOffStartOptions.includes(compOffStartTime)) {
      setCompOffStartTime(compOffStartOptions[0]);
    }
  }, [compOffHours, compOffStartTime, compOffStartOptions]);

  useEffect(() => {
    if (!user?.profile?.id) return;

    // V82.7 Phase 3.3: Firestore direct read 제거 — callable 경유.
    //   본인 (employeeId === uid) + leaveTypeCode='compensatory' 조회는
    //   `getMyCompOffHistories` callable 가 BE 에서 처리.
    //   accessPolicy 자동 적용 (외부망 + TOTP MFA 통과 토큰만 허용).
    const fetchCompOffHistories = async () => {
      setHistoriesLoading(true);
      try {
        const fn = httpsCallable<
          {},
          { success: boolean; histories: LeaveHistory[] }
        >(getFunctions(), 'getMyCompOffHistories');
        const r = await fn({});
        setCompOffHistories(r.data.histories || []);
      } catch (error) {
        console.error('대체휴무 이력 조회 실패:', error);
      } finally {
        setHistoriesLoading(false);
      }
    };

    fetchCompOffHistories();
  }, [user?.profile?.id]);

  // 휴가 신청 제출
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.leaveTypeCode || !formData.startDate || !formData.endDate || !formData.unit) {
      alert('필수 항목을 입력해주세요.');
      return;
    }

    // V66 (boundary): 시작/종료일 비근무일 사전 차단 (BE 가 최종 SSoT)
    if (startBoundaryReasons.length > 0) {
      alert(`휴가 시작일은 근무일이어야 합니다. (${startBoundaryReasons.join(', ')})`);
      return;
    }
    if (endBoundaryReasons.length > 0) {
      alert(`휴가 종료일은 근무일이어야 합니다. (${endBoundaryReasons.join(', ')})`);
      return;
    }

    // V62: CN 연차 잔여일수 초과 신청 FE 사전 차단 (BE가 최종 SSoT)
    if (user?.profile?.companyCode === 'CN' && selectedLeaveType?.deductionType === 'annual' && balance) {
      const remaining = balance.annual.remaining ?? 0;
      if (remaining <= 0) {
        alert('사용 가능한 연차가 없습니다.');
        return;
      }
      // V62: 잔여일수 초과 사전 차단 — 주말 제외 영업일 추정
      // V69 FIX (2026-06-02): UI 는 expandAllowedUnitsForUI 가 'halfDay' → 'morningHalf'+'afternoonHalf' 로 분해 노출.
      //   기존엔 legacy 'halfDay' 라벨만 0.5 로 인식해서 morningHalf/afternoonHalf 가 fullDay 영업일 카운트로
      //   fallthrough → estimatedDays=1 → CN 잔여 0.5 신청 시 오탐 차단. 반차 3종 모두 0.5 로 통일.
      if (formData.startDate && formData.endDate && formData.unit) {
        let estimatedDays = 0;
        if (formData.unit === 'halfDay' || formData.unit === 'morningHalf' || formData.unit === 'afternoonHalf') {
          estimatedDays = 0.5;
        } else if (formData.unit === 'hourly') {
          estimatedDays = (compOffHours || 1) / 8;
        } else {
          // fullDay: 주말 제외 영업일 카운트 (공휴일은 BE에서 최종 판단)
          const start = new Date(formData.startDate);
          const end = new Date(formData.endDate);
          for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const day = d.getDay();
            if (day !== 0 && day !== 6) estimatedDays++;
          }
        }
        if (estimatedDays > remaining) {
          alert(`사용 가능한 연차가 부족합니다. (잔여: ${remaining}일, 예상 신청: ${estimatedDays}일)`);
          return;
        }
      }
    }

    // V280.1: 대체휴무 잔액 초과 사용 FE 사전 차단 (BE가 최종 SSoT)
    if (selectedLeaveType?.deductionType === 'compensatory' && balance) {
      const compRemaining = balance.compensatory?.remaining ?? 0;
      let estimatedDays = 0;
      if (formData.unit === 'fullDay') {
        const start = new Date(formData.startDate!);
        const end = new Date(formData.endDate!);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const day = d.getDay();
          if (day !== 0 && day !== 6) estimatedDays++;
        }
      } else if (formData.unit === 'hourly') {
        estimatedDays = (compOffHours || 1) / 8;
      } else {
        estimatedDays = 0.5;
      }
      if (estimatedDays > compRemaining) {
        alert(`대체휴무 잔액이 부족합니다.\n(잔여: ${formatCompOffDaysHours(compRemaining)}, 예상 신청: ${formatCompOffDaysHours(estimatedDays)})\n보유한 대체휴무 잔액을 초과하여 신청할 수 없습니다.`);
        return;
      }
    }

    try {
      // V33.6: reason 정규화 (undefined 방지)
      // - reason이 없거나 빈 문자열이면 metadata에서 제거
      const normalizedMetadata: Record<string, any> = {};
      if (formData.metadata) {
        for (const [key, value] of Object.entries(formData.metadata)) {
          if (key === 'reason') {
            const reason = typeof value === 'string' ? value.trim() : '';
            if (reason !== '') {
              normalizedMetadata.reason = reason;
            }
            // 빈 문자열이면 필드 생략
          } else if (value !== undefined) {
            normalizedMetadata[key] = value;
          }
        }
      }

      // V70: 첨부파일 URL 추가
      if (uploadedAttachments.length > 0) {
        normalizedMetadata.attachments = uploadedAttachments.map(a => a.url);
      }

      // V60.6: 대체휴무 시간단위 — 시간 정보 metadata에 추가
      if (isCompOffHourly) {
        // V60.6.2: 근무시간 SSoT 기반 범위 검증
        if (!leaveScheduleTimes) {
          alert('근로일정을 조회할 수 없습니다. 날짜를 확인해주세요.');
          return;
        }
        // V280.1: 점심시간 정보 필수 — 누락 시 차단 (단순 덧셈 fallback 방지)
        if (!leaveScheduleTimes.lunchStart || !leaveScheduleTimes.lunchEnd) {
          alert('근로일정에 점심시간 정보가 없습니다. 근로일정을 확인하거나 HR에 문의해주세요.');
          return;
        }
        // V62: 점심시간 스킵 기반 종료시간 계산
        const endTime = calcEndTimeSkippingBreaks(compOffStartTime, compOffHours, leaveScheduleTimes.lunchStart, leaveScheduleTimes.lunchEnd);
        const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return h * 60 + m; };
        const endMin = toMin(endTime);
        const schedEndMin = toMin(leaveScheduleTimes.endTime);
        if (endMin > schedEndMin) {
          alert(`종료시간(${endTime})이 근무시간(${leaveScheduleTimes.endTime})을 초과합니다. 시작시간 또는 사용시간을 조정해주세요.`);
          return;
        }
        // V62: metadata.hours = clock span (BE validation: endMin - startMin === hours * 60)
        const clockSpanHours = (endMin - toMin(compOffStartTime)) / 60;
        normalizedMetadata.hours = clockSpanHours;
        normalizedMetadata.startTime = compOffStartTime;
        normalizedMetadata.endTime = endTime;
      }

      const requestData: LeaveRequestInput = {
        ...formData,
        metadata: normalizedMetadata,
      } as LeaveRequestInput;

      await createLeaveRequest(requestData);
      alert('휴가 신청이 완료되었습니다.');
      setShowRequestForm(false);
      setFormData({
        leaveTypeCode: 'annual',
        unit: 'fullDay',
        metadata: {},
      });
      setUploadedAttachments([]);  // V70: 첨부 초기화
      setCompOffHours(1);  // V60.6: 시간 초기화
      setCompOffStartTime('09:00');
      window.location.reload(); // 새로고침하여 목록 업데이트
    } catch (error) {
      alert(`휴가 신청 실패: ${(error as Error).message}`);
    }
  };

  if (balanceLoading) {
    return <div style={{ padding: '20px' }}>로딩 중...</div>;
  }

  // V55.52.3: 페이지 레이아웃 최적화 - 상단 고정 + 하단 스크롤
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100dvh - 40px - 32px)', // 헤더(40px) + main padding-top(32px)
      minHeight: 0, // flex 내부 스크롤 허용
    }}>
      {/* ===== 상단 고정 영역 ===== */}
      <div style={{ flexShrink: 0 }}>
        {/* 페이지 제목 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px', // V55.52.3: 24→16px
        }}>
          <h1 style={{
            fontSize: '22px', // V55.52.3: 24→22px
            fontWeight: 'bold',
            color: 'var(--color-gray-800)',
          }}>
            내 휴가 관리
          </h1>
          {/* V75.2: 휴가알림 관리 버튼 */}
          <button
            onClick={() => setShowNotifModal(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 16px',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              background: 'white',
              color: 'var(--color-gray-700)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500',
            }}
          >
            <Bell size={16} />
            휴가알림 관리
          </button>
        </div>

        {/* V75.5: 휴가알림 관리 모달 — 화면 중앙 정렬 */}
        {showNotifModal && (
          <ModalOverlay
            onClose={() => setShowNotifModal(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                borderRadius: '12px',
                padding: '24px',
                width: '420px',
                maxWidth: '90vw',
                boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
              }}
            >
              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
                휴가 알림 수신자 관리
              </h3>
              <p style={{ fontSize: '13px', color: 'var(--color-gray-500)', marginBottom: '16px' }}>
                휴가 최종승인 시 메일을 받을 추가 이메일 주소를 입력하세요. (@ngle.co.kr만 가능)
              </p>

              {extraEmails.map((email, idx) => {
                const trimmed = email.trim().toLowerCase();
                const hasError = trimmed !== '' && !trimmed.endsWith('@ngle.co.kr');
                return (
                  <div key={idx} style={{ marginBottom: '6px' }}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => {
                          const next = [...extraEmails];
                          next[idx] = e.target.value;
                          setExtraEmails(next);
                        }}
                        placeholder="example@ngle.co.kr"
                        style={{
                          flex: 1,
                          padding: '8px 12px',
                          border: `1px solid ${hasError ? 'var(--color-red-500)' : 'var(--color-gray-300)'}`,
                          borderRadius: '8px',
                          fontSize: '14px',
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setExtraEmails(extraEmails.filter((_, i) => i !== idx))}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid var(--color-gray-300)',
                          borderRadius: '8px',
                          background: 'white',
                          color: 'var(--color-red-600)',
                          cursor: 'pointer',
                          fontSize: '13px',
                        }}
                      >
                        삭제
                      </button>
                    </div>
                    {hasError && (
                      <p style={{ fontSize: '12px', color: 'var(--color-red-500)', marginTop: '2px', marginLeft: '4px' }}>
                        @ngle.co.kr 이메일만 입력할 수 있습니다
                      </p>
                    )}
                  </div>
                );
              })}

              <button
                type="button"
                onClick={() => setExtraEmails([...extraEmails, ''])}
                style={{
                  padding: '6px 14px',
                  border: '1px solid var(--color-gray-300)',
                  borderRadius: '8px',
                  background: 'var(--color-gray-50)',
                  color: 'var(--color-gray-700)',
                  cursor: 'pointer',
                  fontSize: '13px',
                  marginBottom: '20px',
                }}
              >
                + 추가
              </button>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                <button
                  onClick={() => setShowNotifModal(false)}
                  style={{
                    padding: '8px 20px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '8px',
                    background: 'white',
                    color: 'var(--color-gray-600)',
                    cursor: 'pointer',
                    fontSize: '14px',
                  }}
                >
                  취소
                </button>
                <button
                  onClick={handleSaveNotifEmails}
                  disabled={notifSaving}
                  style={{
                    padding: '8px 20px',
                    border: 'none',
                    borderRadius: '8px',
                    background: 'var(--color-primary)',
                    color: 'white',
                    cursor: notifSaving ? 'not-allowed' : 'pointer',
                    opacity: notifSaving ? 0.6 : 1,
                    fontSize: '14px',
                  }}
                >
                  {notifSaving ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </ModalOverlay>
        )}

        {/* 휴가 잔액 요약 - V55.52.4: 카드 최대 너비 제한
            V69: CN 특수휴가 3종 카드도 동일 grid 에 자연스럽게 wrap — auto-fill 로 변경.
            V69 v2: minmax 축소 — 데스크탑(1320px+)에서 CN 정규직 여성(6 카드) 1줄 노출 가능. */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(165px, 220px))',
          gap: '10px',
          marginBottom: '16px',
        }}>
        {/* 연차 잔액 - V55.52.3: 패딩/아이콘/폰트 축소 */}
        <div style={{
          background: 'white',
          padding: '14px', // V55.52.3: 20→14px
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{
              width: '36px', // V55.52.3: 40→36px
              height: '36px',
              borderRadius: '8px',
              background: 'var(--color-blue-100)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Calendar size={18} style={{ color: 'var(--color-blue-600)' }} />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-600)', marginBottom: '2px' }}>
                연차 잔액
              </div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: ((balance?.annual?.remaining ?? 0) < 0) ? 'var(--color-red-600)' : 'var(--color-gray-800)' }}>
                {balance?.annual?.remaining?.toFixed(1) || '0'} 일
              </div>
            </div>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-gray-500)' }}>
            발생: {balance?.annual?.granted || 0}일 | 사용: {balance?.annual?.used || 0}일
          </div>
          {balance?.annual?.carryOver !== undefined && balance.annual.carryOver !== 0 && (
            <div style={{
              fontSize: '11px',
              color: balance.annual.carryOver < 0 ? 'var(--color-red-600)' : 'var(--color-green-600)',
              marginTop: '2px',
            }}>
              전년도 이월: {balance.annual.carryOver > 0 ? '+' : ''}{balance.annual.carryOver}일
            </div>
          )}
        </div>

        {/* 대체휴무 잔액 - V55.52.3: 패딩/아이콘/폰트 축소 */}
        <div style={{
          background: 'white',
          padding: '14px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: 'var(--color-green-100)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Clock size={18} style={{ color: 'var(--color-green-600)' }} />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-600)', marginBottom: '2px' }}>
                대체휴무 잔액
              </div>
              <div style={{ fontSize: '20px', fontWeight: 'bold', color: 'var(--color-gray-800)' }}>
                {formatCompOffDaysHours(balance?.compensatory?.remaining)}
              </div>
            </div>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--color-gray-500)' }}>
            적립: {formatCompOffDaysHours(balance?.compensatory?.granted)} | 사용: {formatCompOffDaysHours(balance?.compensatory?.used)}
          </div>
        </div>

        {/* 퍼스널힐링데이 - V55.52.3: 패딩/아이콘/폰트 축소
            V68: CN 비정규직(REGULAR 아님) 에게는 카드 자체 미노출. */}
        {!isCnNonRegularForPH && (
        <div style={{
          background: 'white',
          padding: '14px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              background: 'var(--color-purple-100)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <Gift size={18} style={{ color: 'var(--color-purple-600)' }} />
            </div>
            <div>
              <div style={{ fontSize: '13px', color: 'var(--color-gray-600)', marginBottom: '2px' }}>
                퍼스널힐링데이
              </div>
              <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--color-gray-800)' }}>
                분기별 1회 (오후반차)
              </div>
            </div>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)', // V55.52.3: 2→4열로 가로 압축
            gap: '6px',
            fontSize: '11px',
          }}>
            {[1, 2, 3, 4].map((q) => {
              const used = balance?.personalHealing?.[`q${q}Used` as keyof typeof balance.personalHealing];
              return (
                <div
                  key={q}
                  style={{
                    padding: '4px',
                    borderRadius: '4px',
                    background: used ? 'var(--color-gray-100)' : 'var(--color-purple-50)',
                    color: used ? 'var(--color-gray-500)' : 'var(--color-purple-700)',
                    textAlign: 'center',
                  }}
                >
                  {q}Q: {used ? '✓' : '-'}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* V69: CN 특수휴가 3종 (정규직 전용) — 퍼스널힐링 카드 동일 스타일 */}
        {_cc === 'CN' && _etc === 'REGULAR' && (() => {
          // hireDate fallback: firstHireDate → hireDate → ngleFirstHireDate
          const hireDate = (user?.profile as any)?.firstHireDate
            || (user?.profile as any)?.hireDate
            || (user?.profile as any)?.ngleFirstHireDate
            || '';
          // FE 자체 currentPeriod 산출 (entry 없을 때 fallback)
          const computeFallbackPeriod = (hd: string): { periodStart: string; nextResetDate: string } | null => {
            if (!hd) return null;
            const hire = new Date(hd + 'T00:00:00');
            if (isNaN(hire.getTime())) return null;
            const now = new Date();
            let yearIndex = now.getFullYear() - hire.getFullYear();
            const annivThis = new Date(hire); annivThis.setFullYear(now.getFullYear());
            if (now < annivThis) yearIndex--;
            yearIndex = Math.max(0, yearIndex);
            const ps = new Date(hire); ps.setFullYear(hire.getFullYear() + yearIndex);
            const pe = new Date(hire); pe.setFullYear(hire.getFullYear() + yearIndex + 1); pe.setDate(pe.getDate() - 1);
            const nr = new Date(pe); nr.setDate(nr.getDate() + 1);
            const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
            return { periodStart: ymd(ps), nextResetDate: ymd(nr) };
          };
          const fallback = computeFallbackPeriod(hireDate);

          const CARDS: { code: 'womenday'|'birthday'|'healthcn'; name: string; icon: React.ReactNode; bg: string; color: string; chipBg: string; chipColor: string }[] = [
            // V69: 아이콘 v3 — Sparkles → Flower2. 현대적/부드러운 꽃 느낌. orange 톤 유지.
            { code: 'womenday',  name: '여성의 날', icon: <Flower2 size={18} style={{ color: 'var(--color-orange-600, #ea580c)' }} />,   bg: 'var(--color-orange-100, #ffedd5)',  color: 'var(--color-orange-600, #ea580c)',  chipBg: 'var(--color-orange-50, #fff7ed)',  chipColor: 'var(--color-orange-700, #c2410c)' },
            { code: 'birthday',  name: '생일 휴가', icon: <Cake size={18} style={{ color: 'var(--color-amber-600, #d97706)' }} />,       bg: 'var(--color-amber-100, #fef3c7)',   color: 'var(--color-amber-600, #d97706)',   chipBg: 'var(--color-amber-50, #fffbeb)',   chipColor: 'var(--color-amber-700, #b45309)' },
            { code: 'healthcn',  name: '건강검진',  icon: <Stethoscope size={18} style={{ color: 'var(--color-teal-600, #0d9488)' }} />, bg: 'var(--color-teal-100, #ccfbf1)',    color: 'var(--color-teal-600, #0d9488)',    chipBg: 'var(--color-teal-50, #f0fdfa)',    chipColor: 'var(--color-teal-700, #0f766e)' },
          ];

          return (
            <>
              {CARDS.map((card) => {
                // womenday 는 female 만 표시
                if (card.code === 'womenday' && _gender !== 'female') return null;
                const entry = balance?.cnSpecialLeaves?.[card.code];
                let used = false;
                let nextResetDate: string | null = null;
                if (entry && fallback && entry.currentPeriodStart === fallback.periodStart) {
                  used = entry.usedInPeriod === true;
                  nextResetDate = entry.nextResetDate || fallback.nextResetDate;
                } else if (fallback) {
                  nextResetDate = fallback.nextResetDate;
                }
                return (
                  // V69 v2: CN 특수휴가 카드만 padding 축소 (14→12), 아이콘 32px, gap 8px — 다른 카드 무영향, 1줄 fit
                  <div key={card.code} style={{
                    background: 'white',
                    padding: '12px',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: card.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        {card.icon}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '12px', color: 'var(--color-gray-600)', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {card.name}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--color-gray-800)' }}>
                          연 1회
                        </div>
                      </div>
                    </div>
                    <div style={{
                      padding: '3px',
                      borderRadius: '4px',
                      background: used ? 'var(--color-gray-100)' : card.chipBg,
                      color: used ? 'var(--color-gray-500)' : card.chipColor,
                      textAlign: 'center',
                      fontSize: '11px',
                    }}>
                      {used ? '사용완료 ✓' : '미사용'}
                    </div>
                    <div style={{ fontSize: '10px', color: 'var(--color-gray-500)', marginTop: '4px', textAlign: 'center' }}>
                      다음 초기화: {nextResetDate || '-'}
                    </div>
                  </div>
                );
              })}
            </>
          );
        })()}
        </div>
      </div>
      {/* ===== 상단 고정 영역 끝 ===== */}

      {/* ===== 하단 스크롤 영역 ===== */}
      <div style={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
      }}>
        {/* V44: 내 휴가 현황 테이블 */}
        <MyLeaveStatusTable
        balance={balance}
        isLoading={balanceLoading}
        error={null}
        isCnIntern={user?.profile?.companyCode === 'CN' && (user?.profile as any)?.currentEmploymentTypeCode === 'INTERN'}
        hidePersonalHealing={isCnNonRegularForPH}
      />

      {/* 휴가 신청 폼 모달 */}
      {showRequestForm && (
        <ModalOverlay onClose={() => setShowRequestForm(false)} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            padding: '24px',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px' }}>
              휴가 신청
            </h2>
            <form onSubmit={handleSubmit}>
              {/* V62: CN 연차 정책 안내 — V68: 정책 라벨 미노출, 잔액 0 이하일 때만 잔액 부족 경고 유지 */}
              {user?.profile?.companyCode === 'CN' && selectedLeaveType?.deductionType === 'annual' && balance && balance.annual.remaining <= 0 && (
                <div style={{
                  padding: '10px 12px',
                  marginBottom: '16px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  lineHeight: '1.5',
                  background: '#FEF2F2',
                  border: '1px solid #FECACA',
                  color: '#991B1B',
                }}>
                  {`사용 가능한 연차가 없습니다. (잔여: ${balance.annual.remaining}일)`}
                </div>
              )}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  휴가 유형 *
                </label>
                <select
                  value={formData.leaveTypeCode || ''}
                  onChange={(e) => setFormData({ ...formData, leaveTypeCode: e.target.value as LeaveTypeCode })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '4px',
                  }}
                  required
                >
                  {visibleLeaveTypes.map((lt) => (
                    <option key={lt.code} value={lt.code}>
                      {lt.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  시작일 *
                </label>
                <input
                  type="date"
                  value={formData.startDate || ''}
                  min="1900-01-01"
                  max="9999-12-31"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && v.length > 10) return; // V273: 연도 4자리 초과 방지
                    // V66 (sync): 시작일 변경 시 종료일을 시작일과 동일하게 자동 동기화.
                    //   사용자가 이후 종료일을 직접 변경하면 그대로 허용 (endDate input 은 별도 onChange).
                    setFormData({ ...formData, startDate: v, endDate: v });
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: startBoundaryReasons.length > 0
                      ? '1px solid #dc2626'
                      : '1px solid var(--color-gray-300)',
                    borderRadius: '4px',
                  }}
                  required
                />
                {startBoundaryReasons.length > 0 && (
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626' }}>
                    시작일은 근무일이어야 합니다. ({startBoundaryReasons.join(', ')})
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  종료일 *
                </label>
                <input
                  type="date"
                  value={formData.endDate || ''}
                  min="1900-01-01"
                  max="9999-12-31"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v && v.length > 10) return; // V273: 연도 4자리 초과 방지
                    setFormData({ ...formData, endDate: v });
                  }}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: endBoundaryReasons.length > 0
                      ? '1px solid #dc2626'
                      : '1px solid var(--color-gray-300)',
                    borderRadius: '4px',
                  }}
                  required
                />
                {endBoundaryReasons.length > 0 && (
                  <div style={{ marginTop: '4px', fontSize: '12px', color: '#dc2626' }}>
                    종료일은 근무일이어야 합니다. ({endBoundaryReasons.join(', ')})
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  사용 단위 *
                </label>
                {/* V33.5: uiUnits 기반 동적 옵션 렌더링 (halfDay 분해) */}
                <select
                  value={formData.unit || ''}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value as LeaveUnit })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '4px',
                  }}
                  required
                >
                  {uiUnits.map((unit) => (
                    <option key={unit} value={unit}>
                      {UNIT_LABELS[unit]}
                    </option>
                  ))}
                </select>
                {/* V60.6: 대체휴무 시간단위 — 시간수/시작시간/종료시간 선택 */}
                {isCompOffHourly && (
                  <div style={{
                    marginTop: '12px',
                    padding: '12px',
                    background: 'var(--color-blue-50, #eff6ff)',
                    borderRadius: '6px',
                    border: '1px solid var(--color-blue-200, #bfdbfe)',
                  }}>
                    {/* V60.6.2: 근무시간 범위 안내 */}
                    {leaveScheduleTimes && (
                      <div style={{ marginBottom: '10px', fontSize: '12px', color: 'var(--color-gray-600)' }}>
                        근무시간: {leaveScheduleTimes.startTime} ~ {leaveScheduleTimes.endTime}
                      </div>
                    )}
                    {!leaveScheduleTimes && formData.startDate && (
                      <div style={{ marginBottom: '10px', fontSize: '12px', color: 'var(--color-red-600)' }}>
                        해당 날짜의 근로일정이 없습니다. 먼저 근로일정을 신청/승인 받으세요.
                      </div>
                    )}
                    {/* 사용 시간 선택 */}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                        사용 시간
                      </label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => setCompOffHours(Math.max(1, compOffHours - 1))}
                          disabled={compOffHours <= 1}
                          style={{
                            width: '32px', height: '32px', borderRadius: '6px',
                            border: '1px solid var(--color-gray-300)',
                            background: compOffHours <= 1 ? 'var(--color-gray-100)' : 'white',
                            cursor: compOffHours <= 1 ? 'not-allowed' : 'pointer',
                            fontSize: '16px', fontWeight: 'bold',
                          }}
                        >−</button>
                        <span style={{ fontSize: '16px', fontWeight: '600', minWidth: '60px', textAlign: 'center' }}>
                          {compOffHours}시간
                        </span>
                        <button
                          type="button"
                          onClick={() => setCompOffHours(Math.min(8, compOffHours + 1))}
                          disabled={compOffHours >= 8}
                          style={{
                            width: '32px', height: '32px', borderRadius: '6px',
                            border: '1px solid var(--color-gray-300)',
                            background: compOffHours >= 8 ? 'var(--color-gray-100)' : 'white',
                            cursor: compOffHours >= 8 ? 'not-allowed' : 'pointer',
                            fontSize: '16px', fontWeight: 'bold',
                          }}
                        >+</button>
                        <span style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginLeft: '4px' }}>
                          ({compOffHours}시간 차감)
                        </span>
                      </div>
                    </div>

                    {/* 시작시간 선택 */}
                    <div style={{ marginBottom: '10px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500' }}>
                        시작시간
                      </label>
                      <select
                        value={compOffStartTime}
                        onChange={(e) => setCompOffStartTime(e.target.value)}
                        style={{
                          width: '100%', padding: '8px',
                          border: '1px solid var(--color-gray-300)', borderRadius: '4px',
                        }}
                      >
                        {compOffStartOptions.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </div>

                    {/* 종료시간 자동 계산 표시 */}
                    {/* V280.1: lunch 정보가 있을 때만 정확한 계산 표시, 없으면 경고 */}
                    {leaveScheduleTimes?.lunchStart && leaveScheduleTimes?.lunchEnd ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: '8px',
                        padding: '8px 10px', background: 'white', borderRadius: '4px',
                        border: '1px solid var(--color-gray-200)',
                      }}>
                        <Clock size={14} style={{ color: 'var(--color-blue-600)' }} />
                        <span style={{ fontSize: '13px' }}>
                          {compOffStartTime} ~ {calcEndTimeSkippingBreaks(compOffStartTime, compOffHours, leaveScheduleTimes.lunchStart, leaveScheduleTimes.lunchEnd)}
                        </span>
                        <span style={{ fontSize: '12px', color: 'var(--color-gray-500)' }}>
                          (실 {compOffHours}시간, 점심 {leaveScheduleTimes.lunchStart}~{leaveScheduleTimes.lunchEnd} 제외)
                        </span>
                      </div>
                    ) : (
                      <div style={{
                        padding: '8px 10px', background: 'var(--color-red-50, #fef2f2)',
                        borderRadius: '4px', border: '1px solid var(--color-red-200, #fecaca)',
                        fontSize: '12px', color: 'var(--color-red-700, #b91c1c)',
                      }}>
                        근로일정에 점심시간 정보가 없어 종료시간을 계산할 수 없습니다. 근로일정을 확인해주세요.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  사유 {selectedLeaveType?.requiresReason ? '(필수)' : '(선택)'}
                </label>
                <textarea
                  value={formData.metadata?.reason || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    metadata: { ...formData.metadata, reason: e.target.value }
                  })}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '4px',
                    minHeight: '80px',
                  }}
                  placeholder="사유를 입력하세요"
                />
              </div>

              {/* V70: 첨부파일 업로드 */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>
                  첨부파일 {selectedLeaveType?.requiresAttachment ? '(필수)' : '(선택)'}
                </label>
                <input
                  type="file"
                  accept="image/*,.pdf"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const attachment = await uploadFile(file);
                      setUploadedAttachments(prev => [...prev, attachment]);
                    } catch (err: any) {
                      alert(`파일 업로드 실패: ${err.message}`);
                    }
                    e.target.value = '';
                  }}
                  disabled={isUploading}
                  style={{ fontSize: '14px' }}
                />
                {isUploading && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-blue-600)' }}>
                    업로드 중...
                  </div>
                )}
                {uploadedAttachments.length > 0 && (
                  <div style={{ marginTop: '8px' }}>
                    {uploadedAttachments.map((att) => (
                      <div key={att.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '4px 8px',
                        background: 'var(--color-gray-50)',
                        borderRadius: '4px',
                        marginBottom: '4px',
                        fontSize: '13px',
                      }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {att.name} ({(att.size / 1024).toFixed(1)}KB)
                        </span>
                        <button
                          type="button"
                          onClick={() => setUploadedAttachments(prev => prev.filter(a => a.id !== att.id))}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--color-red-500)',
                            cursor: 'pointer',
                            fontSize: '13px',
                            padding: '2px 6px',
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {selectedLeaveType?.requiresAttachment && uploadedAttachments.length === 0 && (
                  <div style={{ marginTop: '6px', fontSize: '12px', color: 'var(--color-red-500)' }}>
                    * 이 휴가 유형은 첨부파일이 필수입니다.
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowRequestForm(false)}
                  style={{
                    padding: '10px 20px',
                    background: 'var(--color-gray-200)',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                  disabled={isSubmitting}
                >
                  취소
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    background: 'var(--color-blue-600)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                  disabled={isSubmitting || isUploading || (!!selectedLeaveType?.requiresAttachment && uploadedAttachments.length === 0)}
                >
                  {isSubmitting ? '처리 중...' : isUploading ? '업로드 중...' : '신청'}
                </button>
              </div>
            </form>
          </div>
        </ModalOverlay>
      )}

      {/* 휴가 신청 내역 - V55.52.3: 패딩 축소 */}
      <div style={{
        background: 'white',
        padding: '16px', // V55.52.3: 20→16px
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        <h2 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '12px' }}>
          휴가 신청 내역
        </h2>

        {requestsLoading ? (
          <div>로딩 중...</div>
        ) : requests && requests.length > 0 ? (
          // V55.52.3: 테이블 셀 패딩 축소 (12→10px)
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  휴가 유형
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  사용 단위
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  기간
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  일수
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  상태
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  신청일
                </th>
                {/* V55: 작업 컬럼 제거 - 취소는 승인요청 페이지에서만 가능 */}
              </tr>
            </thead>
            <tbody>
              {/* V55: 작업 컬럼 제거 - 취소는 승인요청 페이지에서만 가능 */}
              {requests.map((req) => (
                  <tr key={req.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                    <td style={{ padding: '10px', fontSize: '13px' }}>{req.leaveTypeName}</td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>{getLeaveUnitLabel(req.unit)}</td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>
                      {req.startDate} ~ {req.endDate}
                    </td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>
                      {req.leaveTypeCode === 'compensatory'
                        ? formatCompOffDaysHours(req.totalDays)
                        : `${req.totalDays}일`}
                    </td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        background:
                          req.status === 'approved' ? 'var(--color-green-100)' :
                          req.status === 'rejected' ? 'var(--color-red-100)' :
                          req.status === 'cancelled' ? 'var(--color-gray-100)' :
                          req.status === 'cancelPending' ? 'var(--color-orange-100)' :
                          req.status === 'cancelOrgApproved' ? 'var(--color-blue-100)' :
                          req.status === 'orgApproved' ? 'var(--color-blue-100)' :
                          'var(--color-yellow-100)',
                        color:
                          req.status === 'approved' ? 'var(--color-green-700)' :
                          req.status === 'rejected' ? 'var(--color-red-700)' :
                          req.status === 'cancelled' ? 'var(--color-gray-700)' :
                          req.status === 'cancelPending' ? 'var(--color-orange-700)' :
                          req.status === 'cancelOrgApproved' ? 'var(--color-blue-700)' :
                          req.status === 'orgApproved' ? 'var(--color-blue-700)' :
                          'var(--color-yellow-700)',
                      }}>
                        {
                          req.status === 'approved' ? '승인' :
                          req.status === 'rejected' ? '반려' :
                          req.status === 'cancelled' ? '취소됨' :
                          req.status === 'cancelPending' ? '취소 승인 대기' :
                          req.status === 'cancelOrgApproved' ? '취소 조직 승인 완료' :
                          req.status === 'orgApproved' ? '조직 승인 완료' :
                          '대기 중'
                        }
                      </span>
                    </td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>
                      {req.createdAt?.toDate?.()?.toLocaleDateString?.() || '-'}
                    </td>
                  </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-gray-500)', fontSize: '13px' }}>
            신청한 휴가가 없습니다.
          </div>
        )}
      </div>

      {/* Phase 6-2: 대체휴무 이력 - V55.52.3: 패딩/마진 축소 */}
      <div style={{
        background: 'white',
        padding: '16px', // V55.52.3: 20→16px
        borderRadius: '8px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        marginTop: '16px', // V55.52.3: 24→16px
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '12px' }}>
          <History size={18} style={{ color: 'var(--color-green-600)' }} />
          <h2 style={{ fontSize: '16px', fontWeight: 'bold' }}>
            대체휴무 이력
          </h2>
        </div>

        {historiesLoading ? (
          <div>로딩 중...</div>
        ) : compOffHistories && compOffHistories.length > 0 ? (
          /* V55.52.3: 테이블 셀 패딩 축소 (12→10px) */
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-gray-200)' }}>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  구분
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  변경일수
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  변경 후 잔액
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  사유
                </th>
                <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: '600' }}>
                  일시
                </th>
              </tr>
            </thead>
            <tbody>
              {compOffHistories.map((history) => {
                const isAccrual = history.historyType === 'granted';
                const isUsage = history.historyType === 'used';
                const isCancelled = history.historyType === 'cancelled';
                // V70-HOTFIX (2026-10-07): 지급 취소는 historyType='adjustment' + source='cancel_grant' 로 저장됨.
                // 기존 분기 (granted/used/cancelled) 어디에도 매칭되지 않아 빈 배지만 렌더되던 문제 수정.
                const isCancelGrant = history.historyType === 'adjustment' && history.source === 'cancel_grant';

                let typeLabel = '';
                let typeColor = '';
                if (isAccrual) {
                  typeLabel = '발생';
                  typeColor = 'var(--color-green-600)';
                } else if (isUsage) {
                  typeLabel = '사용';
                  typeColor = 'var(--color-red-600)';
                } else if (isCancelled) {
                  typeLabel = '취소 복구';
                  typeColor = 'var(--color-blue-600)';
                } else if (isCancelGrant) {
                  typeLabel = '취소';
                  typeColor = 'var(--color-gray-700)';
                }

                return (
                  <tr key={history.id} style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                    <td style={{ padding: '10px', fontSize: '13px' }}>
                      <span style={{
                        padding: '3px 6px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        background: isAccrual
                          ? 'var(--color-green-100)'
                          : isUsage
                          ? 'var(--color-red-100)'
                          : isCancelGrant
                          ? 'var(--color-gray-200)'
                          : 'var(--color-blue-100)',
                        color: typeColor,
                      }}>
                        {typeLabel}
                      </span>
                      {history.source && (
                        <span style={{ marginLeft: '6px', fontSize: '11px', color: 'var(--color-gray-500)' }}>
                          ({history.source})
                        </span>
                      )}
                    </td>
                    <td style={{
                      padding: '10px',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: history.changeDays > 0 ? 'var(--color-green-600)' : 'var(--color-red-600)',
                    }}>
                      {history.changeDays > 0 ? '+' : ''}{formatCompOffDaysHours(history.changeDays)}
                    </td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>
                      {formatCompOffDaysHours(history.balanceAfter)}
                    </td>
                    <td style={{ padding: '10px', fontSize: '13px', color: 'var(--color-gray-600)' }}>
                      {history.reason || '-'}
                    </td>
                    <td style={{ padding: '10px', fontSize: '13px', color: 'var(--color-gray-500)' }}>
                      {(history.createdAt as Timestamp)?.toDate?.()?.toLocaleDateString?.() || '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: 'center', padding: '24px', color: 'var(--color-gray-500)', fontSize: '13px' }}>
            대체휴무 이력이 없습니다.
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
